import { useState } from "react";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

/**
 * Whether the email went. An account that signs in through its organisation is
 * told apart because it has no password, so nothing the person could retry
 * would send one.
 */
export type PasswordResetOutcome =
  "sent" | "signs_in_through_organisation" | "failed";

type Failure = Exclude<PasswordResetOutcome, "sent">;

/** Asks somebody to confirm emailing a person a link to choose a password. */
export const PasswordResetDialog = ({
  open,
  name,
  onClose,
  onSend,
}: {
  open: boolean;
  /** Whose account it is, so the confirmation says who will receive it. */
  name: string;
  onClose: () => Promisable<void>;
  onSend: () => Promise<PasswordResetOutcome>;
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"sending" | Failure>();
  const sending = status === "sending";

  const close = () => {
    setStatus(undefined);
    void onClose();
  };

  const send = async () => {
    setStatus("sending");
    const outcome = await onSend().catch((): PasswordResetOutcome => "failed");

    if (outcome === "sent") {
      close();
      return;
    }

    setStatus(outcome);
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("admin.users.passwordReset.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {t("admin.users.passwordReset.description", { name })}
          </DialogContentText>
          {status === undefined || status === "sending" ? null : (
            <Alert severity="error">
              {match(status)
                .with("signs_in_through_organisation", () =>
                  t("admin.users.passwordReset.signsInThroughOrganisation"),
                )
                .with("failed", () => t("admin.users.passwordReset.failed"))
                .exhaustive()}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: sending,
        }}
        proceed={{
          label: t("admin.users.passwordReset.confirm"),
          onClick: send,
          disabled: sending,
          kind: "primary",
        }}
      />
    </Dialog>
  );
};
